import { Box, useTheme } from '@mui/material';
import { Workflow } from '../../types';
import { Typography } from '../Typography/Typography';
import { WorkflowStagesStrip } from './WorkflowStagesStrip';

type Props = {
  /** The timesheet owner's workflow; nothing renders when it is missing. */
  workflow?: Workflow | null;
  /** Horizontal alignment of the strip within its row. Defaults to centered. */
  align?: 'center' | 'flex-start';
  /** When true, renders the workflow name above the pipeline strip. */
  showName?: boolean;
};

/**
 * Full-width approval-pipeline strip shown under a single-timesheet header. Reuses the exact
 * WorkflowStagesStrip from the user-form indicator and the Workflows page, so the pipeline (including the
 * per-workflow Submitter label override) reads consistently everywhere. Renders nothing without a workflow.
 */
export function TimesheetWorkflowStrip({ workflow, align = 'center', showName = false }: Props) {
  const { colors } = useTheme();
  if (!workflow) {
    return null;
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: align,
        width: '100%',
        flexBasis: '100%',
        gap: 1.35,
        pt: '4px',
        pb: '16px',
      }}
    >
      {showName && !!workflow.name && (
        <Typography
          variant='caption'
          color={colors.text.secondary}
          style={{ fontWeight: 600, letterSpacing: '0.02em', paddingLeft: 10 }}
        >
          Workflow: {workflow.name}
        </Typography>
      )}
      <WorkflowStagesStrip
        stages={workflow.approvalStages ?? []}
        labels={workflow.stageLabels}
        animationKey={workflow.id}
      />
    </Box>
  );
}
